import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ConfigBaseFilter, SimpleConfigBaseFilter } from 'gung-list';
import { ItemProperties } from '../../components/assortment-tree-navigation/assortment-tree-navigation.component';
import { GungBase, Product } from '../../models';
import { Assortment, AssortmentRecursiveExtended, AssortmentService } from '../assortment.service';
import { MetadataService } from '../metadata/metadata.service';
import { gungComparatorHelper, gungGetPropertyOfObject } from '../../utils/gung-utils';

export class ItemTypeFilterPathField extends SimpleConfigBaseFilter<Product> {
  itemValue: any;
  currentLang = this.translateService.currentLang;
  conceptGroupedPath: string;
  type: string;
  sortFieldValueMap: { [s: string]: any } = {};
  translateValues = this.translateValuesBool;

  constructor(
    protected filterDisplayName: string,
    protected filterTitle: string,
    protected path: string,
    protected notFoundString: string,
    protected translateService?: TranslateService,
    protected metadataService?: MetadataService,
    protected tableName?: string,
    protected valueName?: string,
    protected multiValues?: boolean,
    protected isRange?: boolean,
    protected filterValuePath?: string,
    protected nestedFilterValuePath?: string,
    protected splitString?: string,
    protected filterOptionsSortValueField?: string,
    protected filterOptionsSortingOrder?: string,
    protected translateValuesBool?: boolean
  ) {
    super();
    if (this.isRange) {
      this.type = 'RangeConfigBaseFilter';
    } else if (this.multiValues) {
      this.type = 'ConceptGroupedConfigBaseFilter';
      this.conceptGroupedPath = path;
    } else {
      this.type = 'SimpleConfigBaseFilter';
    }

    if (this.filterOptionsSortingOrder) {
      this.sort = (a, b) => {
        if (this.filterOptionsSortValueField) {
          return gungComparatorHelper(
            this.sortFieldValueMap[a] || '',
            this.sortFieldValueMap[b] || '',
            this.filterOptionsSortingOrder === 'asc' ? 1 : -1
          );
        } else {
          return gungComparatorHelper(a, b, this.filterOptionsSortingOrder === 'asc' ? 1 : -1);
        }
      };
    }
  }

  getName(): string {
    return this.filterTitle;
  }

  getDisplayName(): string {
    return this.filterDisplayName || this.filterTitle;
  }

  getOptionIds(item: GungBase): string[] {
    this.itemValue = undefined;
    if (item.hasOwnProperty('product')) {
      // Concept detail Filter
      item = (item as any).product;
    }
    if (item === undefined || item === null) {
      return [this.notFoundString];
    }
    let valuesAsArray = [];
    const splitPath = ['extra', ...this.path.split('.')];
    const reducedValue = this.getReduceValueObject(splitPath, item);

    if (reducedValue === undefined || reducedValue === null) {
      return [this.notFoundString];
    } else if (!this.multiValues) {
      if (reducedValue && reducedValue.properties && reducedValue.properties.sorting && reducedValue.code) {
        // nested values (used in sorting)
        valuesAsArray = [reducedValue.code];
      } else if (Array.isArray(reducedValue)) {
        valuesAsArray = reducedValue.map(v => {
          let res = v;
          if (this.isPIMMetadata(v)) {
            res = this.extractPIMMetadataValue(v);

            if (!!this.filterOptionsSortingOrder && !!this.filterOptionsSortValueField) {
              this.saveSortValueFieldMap(v, res);
            }
          }
          return res;
        });
      } else if (this.isPIMMetadata(reducedValue)) {
        const extractedValue = this.extractPIMMetadataValue(reducedValue);

        if (!!this.filterOptionsSortingOrder && !!this.filterOptionsSortValueField) {
          this.saveSortValueFieldMap(reducedValue, extractedValue);
        }

        if (Array.isArray(extractedValue)) {
          valuesAsArray = extractedValue;
        } else {
          valuesAsArray = [extractedValue];
        }
      } else {
        if (!!this.splitString) {
          valuesAsArray = [...reducedValue.split(this.splitString)];
        } else {
          valuesAsArray = [reducedValue];
        }
      }
    } else {
      if (Array.isArray(reducedValue)) {
        valuesAsArray = reducedValue.map(v => {
          let res = v;
          if (v.hasOwnProperty('description')) {
            res = v.description || this.notFoundString;
          }
          return res;
        });
      } else if (reducedValue.description) {
        // PIM metadata
        valuesAsArray = [reducedValue.description];
      } else {
        valuesAsArray = [reducedValue];
      }
    }
    this.itemValue = reducedValue;
    const mappedValues = valuesAsArray.map(value => {
      if (typeof value === 'string' || value instanceof String) {
        value = value.replace(/^\s+|\s+$/g, '');
      }
      if (!value) {
        return this.notFoundString;
      } else {
        if (this.valueName && typeof value === 'object' && Object.keys(value).indexOf(this.valueName) >= 0) {
          return value[this.valueName];
        } else {
          return value;
        }
      }
    });
    return [...new Set<any>(mappedValues)];
  }

  isPIMMetadata(item: any): boolean {
    return (
      item &&
      item.hasOwnProperty('description') &&
      item.hasOwnProperty('properties') &&
      item.hasOwnProperty('translateProperties')
    );
  }

  extractPIMMetadataValue(item: GungBase): string | string[] {
    let valuePath = 'description';
    if (!!this.filterValuePath) {
      valuePath = this.filterValuePath;

      const valueObject = gungGetPropertyOfObject(valuePath, item);
      if (valueObject instanceof Array) {
        const nestedValuePath = this.nestedFilterValuePath || 'description';
        return valueObject.map(v => gungGetPropertyOfObject(nestedValuePath, v)).filter(v => !!v);
      }

      if (!!this.nestedFilterValuePath) {
        valuePath += '.' + this.nestedFilterValuePath;
      }
    }
    return gungGetPropertyOfObject(valuePath, item);
  }

  getTranslatedDescription(obj) {
    let translatedDescriptionString = 'translatedDescription';
    if (
      obj &&
      obj.translateProperties &&
      obj.translateProperties[this.currentLang] &&
      obj.translateProperties[this.currentLang][translatedDescriptionString] &&
      obj.translateProperties[this.currentLang][translatedDescriptionString] !== ''
    ) {
      return obj.translateProperties[this.currentLang][translatedDescriptionString];
    }

    return obj?.description || null;
  }

  getOptionName(key: string): string {
    if (!key) {
      return '';
    }

    //////////
    // This part should be safe to remove after moving the value path fetch to getOptionIds but I am scared
    if (this.filterValuePath && !this.nestedFilterValuePath) {
      let varToTranslate = this.itemValue;
      if (varToTranslate instanceof Array) {
        const findTranslateInArray = varToTranslate.find(v => v.description === key);
        if (findTranslateInArray) {
          varToTranslate = findTranslateInArray;
        }
      }
      const tranlsatedValue = this.getReduceValueObject(this.filterValuePath.split('.'), varToTranslate);
      if (!!tranlsatedValue) {
        return tranlsatedValue;
      }
    }
    //////////

    if (!this.tableName || !this.valueName) {
      return key;
    }
    if (
      this.itemValue &&
      this.valueName &&
      typeof this.itemValue === 'object' &&
      Object.keys(this.itemValue).indexOf(this.valueName) >= 0
    ) {
      return this.itemValue[this.valueName];
    }
    return this.metadataService.getMetadataValue(this.tableName, this.valueName, key);
  }

  getSortingIndex(optionId?: string): string {
    if (Array.isArray(this.itemValue)) {
      const option = this.itemValue.find(value => {
        return value.description === optionId;
      });
      if (option?.properties?.sorting) {
        return option.properties.sorting;
      }
    } else if (this.itemValue?.properties?.sorting) {
      return this.itemValue.properties.sorting; // sorting index
    }
    return;
  }

  getReduceValueObject(splitPath, item): any {
    const translatedDescription = this.getTranslatedDescription(item);
    if (translatedDescription) {
      return translatedDescription;
    }
    if (splitPath.indexOf('i18n') > -1) {
      splitPath.splice(splitPath.indexOf('i18n') + 1, 0, this.currentLang);
    }
    const reducedValue = splitPath.reduce((oldValue, currentValue) => {
      if (!oldValue) {
        return oldValue;
      }
      if (oldValue[currentValue]) {
        return oldValue[currentValue];
      } else {
        return null;
      }
    }, item);
    if (
      !reducedValue ||
      (typeof reducedValue === 'object' &&
        Object.keys(reducedValue).length === 0 &&
        reducedValue.constructor === Object)
    ) {
      // Empty Object found
      return null;
    }
    return reducedValue;
  }

  saveSortValueFieldMap(metadata: any, id: string | string[]): void {
    if (this.filterOptionsSortValueField) {
      if (Array.isArray(id)) {
        for (const i of id) {
          this.sortFieldValueMap[i] = gungGetPropertyOfObject(this.filterOptionsSortValueField, metadata) || '';
        }
      } else {
        this.sortFieldValueMap[id] = gungGetPropertyOfObject(this.filterOptionsSortValueField, metadata) || '';
      }
    }
  }
}

export class PimNumberFilter extends SimpleConfigBaseFilter<Product> {
  type = this.isRange ? 'RangeConfigBaseFilter' : 'SimpleConfigBaseFilter';

  constructor(
    protected filterDisplayName: string,
    protected filterTitle: string,
    protected field: string,
    protected multiValues?: boolean,
    protected isRange?: boolean
  ) {
    super();
  }

  getName(): string {
    return this.filterTitle;
  }

  getDisplayName(): string {
    return this.filterDisplayName || this.filterTitle;
  }

  getOptionIds(item: Product): string[] {
    let value = item.extra.pim?.[this.field];
    if (!value) {
      value = 'UNDEFINED';
    }
    let ids = ['' + value];
    if (this.multiValues) {
      if (Array.isArray(item.extra.pim[this.field])) {
        ids = (item.extra.pim[this.field] as any[]).map(value => '' + value);
      }
    }
    return ids;
  }

  getOptionName(key: string): string {
    return key;
  }

  sort = (a, b) => parseFloat(a) - parseFloat(b);
}

export class PimHexcodeFilter extends SimpleConfigBaseFilter<Product> {
  type = 'ColorConfigBaseFilter';

  constructor(
    protected filterDisplayName: string,
    protected filterTitle: string,
    protected field: string,
    protected multiValues?: boolean
  ) {
    super();

    // Custom sort method for hex colors - sorts by hue, then lightness
    this.sort = (a: string, b: string) => {
      // Validate hex color format (must be #RRGGBB)
      const hexPattern = /^#[0-9A-F]{6}$/i;
      const isValidA = a && typeof a === 'string' && hexPattern.test(a);
      const isValidB = b && typeof b === 'string' && hexPattern.test(b);

      // Move invalid colors to the end
      if (!isValidA && !isValidB) return 0;
      if (!isValidA) return 1;
      if (!isValidB) return -1;

      const hslA = this.hexToHSL(a);
      const hslB = this.hexToHSL(b);

      // Treat low saturation colors (grays) specially - put them at the end
      const isGrayA = hslA.s < 0.15;
      const isGrayB = hslB.s < 0.15;

      if (isGrayA && !isGrayB) return 1;
      if (!isGrayA && isGrayB) return -1;

      // Both grays - sort by lightness only (lighter first)
      if (isGrayA && isGrayB) {
        return hslB.l - hslA.l;
      }

      // Both are colors - sort by hue, then lightness, then saturation
      const hueDiff = hslA.h - hslB.h;
      if (Math.abs(hueDiff) > 1) {
        return hueDiff;
      }

      const lightnessDiff = hslB.l - hslA.l;
      if (Math.abs(lightnessDiff) > 0.05) {
        return lightnessDiff;
      }

      return hslB.s - hslA.s;
    };
  }

  getSortField(): string {
    return 'valueName';
  }

  getName(): string {
    return this.filterTitle;
  }

  getDisplayName(): string {
    return this.filterDisplayName || this.filterTitle;
  }

  getOptionIds(item: Product): string[] {
    let value = item.extra.pim?.[this.field];
    if (!value) {
      return [];
    }

    // Return original hex colors directly (no grouping)
    let ids = ['' + value];
    if (this.multiValues) {
      if (Array.isArray(item.extra.pim[this.field])) {
        ids = (item.extra.pim[this.field] as any[]).map(value => '' + value);
      }
    }

    // Filter out invalid hex colors (must be #RRGGBB format)
    const hexPattern = /^#[0-9A-F]{6}$/i;
    ids = ids.filter(id => hexPattern.test(id));

    return ids;
  }

  getOptionName(key: string): string {
    // Return the hex color as-is for display
    return key;
  }

  /**
   * Convert hex color to HSL (Hue, Saturation, Lightness)
   * @param hex - Hex color string (e.g., "#FF5733" or "FF5733")
   * @returns Object with h (0-360), s (0-1), l (0-1)
   */
  private hexToHSL(hex: string): { h: number; s: number; l: number } {
    // Remove # if present
    hex = hex.replace(/^#/, '');

    // Convert to RGB
    const r = parseInt(hex.substring(0, 2), 16) / 255;
    const g = parseInt(hex.substring(2, 4), 16) / 255;
    const b = parseInt(hex.substring(4, 6), 16) / 255;

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const delta = max - min;

    let h = 0;
    let s = 0;
    const l = (max + min) / 2;

    if (delta !== 0) {
      s = l > 0.5 ? delta / (2 - max - min) : delta / (max + min);

      switch (max) {
        case r:
          h = ((g - b) / delta + (g < b ? 6 : 0)) / 6;
          break;
        case g:
          h = ((b - r) / delta + 2) / 6;
          break;
        case b:
          h = ((r - g) / delta + 4) / 6;
          break;
      }
    }

    return {
      h: h * 360, // Convert to degrees (0-360)
      s: s,       // 0-1
      l: l        // 0-1
    };
  }
}

@Injectable({
  providedIn: 'root'
})
export class CloudPimFiltersService {
  currentLang = this.translateService.currentLang;
  splitString: string;

  constructor(
    protected translateService: TranslateService,
    protected assortmentService: AssortmentService,
    protected metadataService: MetadataService
  ) {}

  getFilters(
    assortment: Assortment,
    multiValues?: boolean,
    assortmentLeaf?: AssortmentRecursiveExtended,
    hideUndefined?: boolean,
    translateValues: boolean = true
  ): ConfigBaseFilter<Product>[] {
    const filters: ConfigBaseFilter<Product>[] = [];
    if (!!assortment && (assortment.extra.itemProperties || assortment.extra.skuProperties)) {
      let properties: ItemProperties[] = assortment.extra.itemProperties || assortment.extra.skuProperties;
      if (assortmentLeaf && this.assortmentService.isGroupedAssortment(assortmentLeaf)) {
        properties = assortmentLeaf.extra.itemProperties;
      }
      for (const property of properties) {
        if (/* property.isDisplay &&  */ property.isFilter) {
          // const isRange = property.path === 'pim.import_indrediameter'; // REMOVE THIS AND UNCOMMENT NEXT LINE
          const isRange = property.filterType && property.filterType === 'RANGE';
          const isHexcode = property.filterType && property.filterType === 'HEX_COLOR';

          const field = property.path.split('.')[1];
          const translationKey = property.translationKey;
          const displayName = property.displayName;

          // Check if translation exists
          const undefinedString = 'NO_' + translationKey + '_DEFINED';
          const undefinedTranslation = hideUndefined
            ? undefined
            : this.translateService.instant(undefinedString) === undefinedString
              ? 'UNDEFINED'
              : undefinedString;

          const filterOptionsSortValueField: string = property?.extra?.filterOptionsValueField;
          const filterOptionsSortingOrder: string = property?.extra?.filterOptionsSortingOrder;

          if (isHexcode) {
            filters.push(new PimHexcodeFilter(displayName, translationKey, field, multiValues));
          } else if (property.type === 'number') {
            filters.push(new PimNumberFilter(displayName, translationKey, field, multiValues, isRange));
          } else if (property.type === 'metadata') {
            if (property.metaReference) {
              filters.push(
                new ItemTypeFilterPathField(
                  displayName,
                  translationKey,
                  property.path,
                  undefinedTranslation,
                  this.translateService,
                  this.metadataService,
                  property.metaReference.table,
                  property.metaReference.field,
                  multiValues,
                  isRange,
                  undefined,
                  undefined,
                  this.splitString,
                  filterOptionsSortValueField,
                  filterOptionsSortingOrder,
                  translateValues
                )
              );
            } else if (property.metadata && property.metadata.split('.').length === 2) {
              const splitmeta = property.metadata.split('.');
              const metaTable = splitmeta[0];
              const metaField = splitmeta[1];
              filters.push(
                new ItemTypeFilterPathField(
                  displayName,
                  translationKey,
                  property.path,
                  undefinedTranslation,
                  this.translateService,
                  this.metadataService,
                  metaTable,
                  metaField,
                  multiValues,
                  isRange,
                  undefined,
                  undefined,
                  this.splitString,
                  filterOptionsSortValueField,
                  filterOptionsSortingOrder,
                  translateValues
                )
              );
            } else {
              let filterValuePath = property.filterValueField || 'description';
              if (filterValuePath.indexOf('translateProperties') > -1) {
                const splittedPath = filterValuePath.split('.');
                splittedPath.splice(splittedPath.indexOf('translateProperties') + 1, 0, this.currentLang);
                filterValuePath = splittedPath.join('.');
              }
              let nestedFilterValuePath = property.nestedFilterValueField;
              if (nestedFilterValuePath && nestedFilterValuePath.indexOf('translateProperties') > -1) {
                const splittedPath = nestedFilterValuePath.split('.');
                splittedPath.splice(splittedPath.indexOf('translateProperties') + 1, 0, this.currentLang);
                nestedFilterValuePath = splittedPath.join('.');
              }
              filters.push(
                new ItemTypeFilterPathField(
                  displayName,
                  translationKey,
                  property.path,
                  undefinedTranslation,
                  this.translateService,
                  this.metadataService,
                  null,
                  null,
                  multiValues,
                  isRange,
                  filterValuePath,
                  nestedFilterValuePath,
                  this.splitString,
                  filterOptionsSortValueField,
                  filterOptionsSortingOrder,
                  translateValues
                )
              );
            }
          } else {
            filters.push(
              new ItemTypeFilterPathField(
                displayName,
                translationKey,
                property.path,
                undefinedTranslation,
                this.translateService,
                null,
                null,
                null,
                multiValues,
                isRange,
                undefined,
                undefined,
                this.splitString,
                filterOptionsSortValueField,
                filterOptionsSortingOrder,
                translateValues
              )
            );
          }
        }
      }
    }

    return filters;
  }
}
